import { inject, onUnmounted } from 'vue';
import type { RealtimeEvent, RealtimeEventMap } from '@taxi/shared';
import type { Realtime } from './Realtime';

export const REALTIME_KEY = Symbol('realtime');

/**
 * Accès au canal temps réel de l'application.
 *
 * Les abonnements pris via ce composable sont automatiquement résiliés au
 * démontage du composant : impossible d'accumuler des gestionnaires fantômes en
 * naviguant entre les écrans.
 */
export function useRealtime() {
	const realtime = inject<Realtime>(REALTIME_KEY);
	if (!realtime) {
		throw new Error("Le canal temps réel n'est pas installé sur cette application.");
	}

	const unsubscribes: (() => void)[] = [];

	const on = <E extends RealtimeEvent>(
		event: E,
		handler: (payload: RealtimeEventMap[E]) => void,
	): void => {
		unsubscribes.push(realtime.on(event, handler));
	};

	/** S'abonne au repli par interrogation (appelé quand la socket est indisponible). */
	const onPoll = (handler: () => void): void => {
		const subscription = realtime.pollObserver.subscribe(handler);
		unsubscribes.push(() => subscription.unsubscribe());
	};

	onUnmounted(() => {
		unsubscribes.forEach((unsubscribe) => unsubscribe());
	});

	return { realtime, on, onPoll };
}
